import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  createErrorResult,
  ERRORS,
  KEY_SESSION_EXPIRY_CHECK_INTERVAL_MS,
} from "@prostocheck/shared";

import {
  clearPersistedSession,
  isPersistedSessionExpired,
  loadPersistedSession,
  readPersistedSessionMeta,
  restoreSessionFromStorage,
} from "../../api";
import { notifyError } from "../../notifications";
import { useSession } from "../use-session";

/**
 * Two jobs, both background: once at app start, rebuild a session from
 * whatever `readKey`+`signIn` left in `localStorage` (`useLogin` writes it
 * there via `savePersistedSession`); for as long as the tab stays open,
 * re-check the signed-in key's certificate against the clock and sign the
 * user out the moment it lapses. See ai/docs/kep-signing.md for why a
 * session can be restored at all, which this repo did not allow before.
 */
export const useRestoredSession = (): void => {
  const { t } = useTranslation();
  const { session, startSession, endSession, setIsRestoringSession } =
    useSession();

  useEffect(() => {
    if (session) return;

    const meta = readPersistedSessionMeta();

    if (!meta || isPersistedSessionExpired(meta)) {
      if (meta) clearPersistedSession();

      setIsRestoringSession(false);

      return;
    }

    let isCurrent = true;

    const restore = async () => {
      // The only async step: decrypting the credential with crypto.subtle.
      const stored = await loadPersistedSession();

      if (!isCurrent) return;

      if (!stored) {
        clearPersistedSession();
        setIsRestoringSession(false);

        return;
      }

      const result = await restoreSessionFromStorage(stored);

      if (!isCurrent) return;

      if (result.success && result.data) {
        startSession(result.data);
      } else {
        clearPersistedSession();
      }

      setIsRestoringSession(false);
    };

    void restore();

    return () => {
      isCurrent = false;
    };
    // Restoring is a one-time, app-start action, so this intentionally runs
    // only once and does not react to `session` changing afterwards.
  }, []);

  useEffect(() => {
    if (!session) return;

    const checkExpiry = () => {
      if (new Date(session.owner.validTo).getTime() > Date.now()) return;

      endSession();
      notifyError({ result: createErrorResult(ERRORS.key.sessionExpired), t });
    };

    checkExpiry();

    const interval = setInterval(
      checkExpiry,
      KEY_SESSION_EXPIRY_CHECK_INTERVAL_MS,
    );

    return () => clearInterval(interval);
  }, [session, endSession, t]);
};
